import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mic, Building2, Bot, Layers, FlaskConical,
  Lightbulb, Rocket, Cog, BarChart3, MessageCircle, ShieldCheck, TrendingUp,
  ArrowRight, Check, ChevronLeft, ChevronRight,
  PhoneCall, Users, Search, BarChart2, Workflow, FileSearch, Smile,
  Home, TrendingUp as TrendingUpIcon, ClipboardCheck, CreditCard,
  Zap, FileText, UserCheck, Activity,
  Server, Database, Cpu, Code2, Globe,
  BookOpen, Microscope, Clock, ShieldCheck as ShieldCheckIcon,
} from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import FloatingOrbs from "@/components/FloatingOrbs";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Ecosystem Solutions (owned by brgt.ai) ──────────────────────────────────
const ecosystemSolutions = [
  {
    id: "voice-ai",
    icon: Mic,
    title: "Voice AI & Conversational Sales",
    tagline: "Never miss a high-value lead again.",
    description:
      "brgt.ai deploys a coordinated team of voice AI agents that work together around the clock — qualifying inbound leads through natural conversation, nurturing prospects with context-aware follow-ups, and passing sales-ready opportunities to your team at exactly the right moment.",
    accentColor: "text-indigo-400",
    accentBg: "bg-indigo-500/10",
    accentBorder: "border-indigo-500/25",
    gradientFrom: "from-indigo-600",
    gradientTo: "to-violet-600",
    businessImpact: [
      { icon: PhoneCall, label: "24/7 inbound coverage", sub: "No missed calls, ever" },
      { icon: Users, label: "3× lead conversion", sub: "Qualified at scale" },
      { icon: Smile, label: "Human-like UX", sub: "Natural, trust-building dialogue" },
    ],
    capabilities: [
      "AI Voice Agents — lifelike outbound & inbound calling",
      "Lead Qualification Automation — score and prioritize prospects automatically",
      "Multi-Agent Call Orchestration — agents collaborate on complex scenarios",
      "Human Handoff Intelligence — seamless transfer at peak buying intent",
    ],
    useCases: ["Sales & Revenue Teams", "Customer Support Centers", "Real Estate Outreach", "Healthcare Scheduling"],
    illustration: (
      <div className="relative w-full h-48 flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-violet-600/10 rounded-2xl" />
        <div className="relative flex items-end gap-3">
          {[40, 60, 45, 72, 55, 68, 50].map((h, i) => (
            <motion.div
              key={i}
              className="w-5 rounded-t-md bg-indigo-500/70"
              style={{ height: h }}
              animate={{ height: [h, h * 1.3, h] }}
              transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }}
            />
          ))}
        </div>
        <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-indigo-500/20 border border-indigo-500/30 rounded-full px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
          <span className="text-xs text-indigo-300 font-medium">Agents Active</span>
        </div>
      </div>
    ),
  },
  {
    id: "real-estate",
    icon: Building2,
    title: "Intelligent Real Estate Technology",
    tagline: "From search to close — AI at every step.",
    description:
      "brgt.ai delivers a full-spectrum real estate intelligence platform that reimagines how buyers, sellers, and agents interact with property data. AI-powered search, neighbourhood analytics, automated workflows, and pre-approval integration combine to reduce time-to-close and elevate the client experience.",
    accentColor: "text-amber-400",
    accentBg: "bg-amber-500/10",
    accentBorder: "border-amber-500/25",
    gradientFrom: "from-amber-500",
    gradientTo: "to-orange-500",
    businessImpact: [
      { icon: Home, label: "Faster property discovery", sub: "AI-matched listings" },
      { icon: TrendingUpIcon, label: "Higher sale prices", sub: "Expert-driven strategy" },
      { icon: ClipboardCheck, label: "Smoother closings", sub: "End-to-end automation" },
    ],
    capabilities: [
      "AI-Powered Property Search — 10+ smart filters, area insights, school ratings",
      "Market Analytics & Trends — real-time pricing, demand signals, comparable sales",
      "Buyer & Seller Journey Automation — automated updates, reminders, and document flows",
      "Pre-Approval Integration — connect buyers to financing at the right moment",
    ],
    useCases: ["Real Estate Brokerages", "Property Portals", "Mortgage Lenders", "PropTech Startups"],
    illustration: (
      <div className="relative w-full h-48 flex items-center justify-center overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/15 to-orange-500/10 rounded-2xl" />
        <div className="relative grid grid-cols-3 gap-3 px-4 w-full">
          {["$1.2M", "$895K", "$2.1M", "$650K", "$1.8M", "$750K"].map((price, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15 }}
              className="bg-white/10 border border-white/10 rounded-lg p-2 text-center"
            >
              <div className="w-full h-6 bg-amber-500/20 rounded mb-1" />
              <span className="text-xs text-amber-300 font-semibold">{price}</span>
            </motion.div>
          ))}
        </div>
        <div className="absolute top-3 right-3 bg-amber-500/20 border border-amber-500/30 rounded-full px-2 py-0.5">
          <span className="text-xs text-amber-300 font-medium">Live Listings</span>
        </div>
      </div>
    ),
  },
  {
    id: "conversational-ai",
    icon: Bot,
    title: "Conversational AI & Agentic Process Automation",
    tagline: "Automate anything. Code nothing.",
    description:
      "brgt.ai's zero-code agentic platform unifies conversational AI, robotic process automation, document intelligence, and interactive avatars into a single suite. HR, finance, customer support, and operations teams can deploy autonomous workflows and chatbots without writing a single line of code.",
    accentColor: "text-violet-400",
    accentBg: "bg-violet-500/10",
    accentBorder: "border-violet-500/25",
    gradientFrom: "from-violet-600",
    gradientTo: "to-purple-600",
    businessImpact: [
      { icon: Zap, label: "80% faster task completion", sub: "Automated workflows" },
      { icon: FileText, label: "Instant document AI", sub: "Extract, summarize, reason" },
      { icon: UserCheck, label: "Higher employee NPS", sub: "Eliminate repetitive work" },
    ],
    capabilities: [
      "Zero-Code Agentic AI — build and deploy agents without engineering support",
      "Conversational RPA — trigger complex workflows through natural dialogue",
      "Document Intelligence — extract, summarize, and reason over business documents",
      "HR & Operations Bots — automate onboarding, payroll queries, and IT tickets",
      "Interactive Avatars — humanize digital touchpoints with lifelike multimodal experiences",
    ],
    useCases: ["HR & People Operations", "Finance & Accounting", "Customer Support", "IT Service Desks"],
    illustration: (
      <div className="relative w-full h-48 flex flex-col gap-2 justify-center px-4 overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-600/15 to-purple-600/10 rounded-2xl" />
        {[
          { label: "Employee onboarding bot", status: "Running", color: "bg-violet-400" },
          { label: "Invoice processing agent", status: "Complete", color: "bg-emerald-400" },
          { label: "Support ticket router", status: "Running", color: "bg-violet-400" },
          { label: "Document summarizer", status: "Queued", color: "bg-amber-400" },
        ].map((item, i) => (
          <div key={i} className="relative flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
            <span className={`w-2 h-2 rounded-full ${item.color} ${item.status === "Running" ? "animate-pulse" : ""}`} />
            <span className="text-xs text-slate-300 flex-1">{item.label}</span>
            <span className="text-xs text-slate-500">{item.status}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "full-stack-ai",
    icon: Layers,
    title: "Full-Stack AI Product Engineering",
    tagline: "From idea to enterprise-grade deployment.",
    description:
      "brgt.ai engineers end-to-end technology products for pharmaceutical, fintech, agrotech, and enterprise clients. From MVP scoping and data engineering to IoT integration, low-code platforms, and cloud-native infrastructure — we own the full delivery lifecycle so your team stays focused on the business.",
    accentColor: "text-emerald-400",
    accentBg: "bg-emerald-500/10",
    accentBorder: "border-emerald-500/25",
    gradientFrom: "from-emerald-600",
    gradientTo: "to-teal-600",
    businessImpact: [
      { icon: Server, label: "Cloud-native delivery", sub: "Scalable infrastructure" },
      { icon: Database, label: "Unified data layer", sub: "Clean, governed data" },
      { icon: Activity, label: "Faster time-to-market", sub: "MVP to production fast" },
    ],
    capabilities: [
      "Managed IT Services — proactive monitoring, maintenance, and support",
      "Data Engineering — pipelines, warehouses, and governance frameworks",
      "IoT Development & Consulting — sensor integration, edge computing, real-time data",
      "Low-Code / No-Code Platforms — business-user-friendly app development",
      "Industry-Vertical Solutions — purpose-built for Pharma, Fintech, Agrotech & more",
    ],
    useCases: ["Pharmaceutical & Life Sciences", "Financial Services", "Agricultural Technology", "Manufacturing & Industry 4.0"],
    illustration: (
      <div className="relative w-full h-48 flex items-center justify-center overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/15 to-teal-600/10 rounded-2xl" />
        <div className="relative flex flex-col items-center gap-2 w-full px-4">
          {[
            { label: "Cloud Infrastructure", pct: 92 },
            { label: "Data Pipelines", pct: 78 },
            { label: "API Integrations", pct: 85 },
            { label: "IoT Devices", pct: 64 },
          ].map((item, i) => (
            <div key={i} className="w-full">
              <div className="flex justify-between mb-0.5">
                <span className="text-xs text-slate-400">{item.label}</span>
                <span className="text-xs text-emerald-400">{item.pct}%</span>
              </div>
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-emerald-500 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${item.pct}%` }}
                  transition={{ duration: 1, delay: i * 0.15 }}
                  viewport={{ once: true }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "deep-tech",
    icon: FlaskConical,
    title: "Deep Tech Research Intelligence",
    tagline: "Evidence-based answers for complex technical decisions.",
    description:
      "brgt.ai's research intelligence engine is built for deep tech startups and R&D teams that can't afford to base decisions on shallow search results. It indexes primary sources — research papers, patents, official documentation — and delivers precise, contextual, domain-specific answers that accelerate R&D workflows by up to 35%.",
    accentColor: "text-sky-400",
    accentBg: "bg-sky-500/10",
    accentBorder: "border-sky-500/25",
    gradientFrom: "from-sky-600",
    gradientTo: "to-cyan-600",
    businessImpact: [
      { icon: Clock, label: "20–35% R&D time saved", sub: "Less search, more build" },
      { icon: BookOpen, label: "Primary-source answers", sub: "Papers, patents, docs" },
      { icon: Microscope, label: "Domain-specific accuracy", sub: "Trained SLMs per field" },
    ],
    capabilities: [
      "Evidence-Based AI Search — every answer backed by verifiable primary sources",
      "Domain-Specific Language Models — trained per field for technical precision",
      "R&D Workflow Acceleration — cut research time by up to 35%",
      "Technical Architecture Validation — benchmark designs against corroborated data",
      "Multi-Source Evidence Retrieval — cross-domain synthesis in a single query",
    ],
    useCases: ["Deep Tech Startups", "Corporate R&D Labs", "University Research Teams", "Defence & Aerospace"],
    illustration: (
      <div className="relative w-full h-48 flex flex-col justify-center px-4 overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/15 to-cyan-600/10 rounded-2xl" />
        <div className="relative space-y-2">
          <div className="text-xs text-slate-400 mb-2 font-mono">Query: "quantum error correction thresholds"</div>
          {[
            { source: "Nature Physics 2024", score: 98, label: "Surface code threshold analysis" },
            { source: "arXiv 2403.12847", score: 94, label: "Fault-tolerant qubit architecture" },
            { source: "IBM Research Patent", score: 91, label: "Dynamic decoupling protocol" },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.12 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5"
            >
              <span className="text-sky-400 font-bold text-xs w-8">{item.score}%</span>
              <div>
                <div className="text-xs text-white">{item.label}</div>
                <div className="text-xs text-slate-500">{item.source}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
];

// ─── Service flashcards (for carousel at bottom) ──────────────────────────────
const serviceFlashcards = [
  { icon: Lightbulb, title: "Strategy & Consulting", services: ["AI Consulting", "AI Strategy", "PoC Development"], accent: "text-amber", bg: "bg-amber/10", border: "border-amber/20" },
  { icon: Rocket, title: "Build & Deploy", services: ["Custom AI Solutions", "Agent Deployment", "Solution Architecture", "System Integration"], accent: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/20" },
  { icon: Cog, title: "Automation & Operations", services: ["Agentic Automation", "Workflow Automation", "Process Monitoring"], accent: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
  { icon: BarChart3, title: "Intelligence & Insights", services: ["Document Intelligence", "Competitive Intelligence", "Data Analytics", "Research Synthesis"], accent: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20" },
  { icon: MessageCircle, title: "Conversational AI", services: ["Chatbot Development", "Document Validation", "AI Integration"], accent: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  { icon: ShieldCheck, title: "Responsible AI", services: ["AI Compliance", "Ethical AI Frameworks", "Bias Auditing"], accent: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/20" },
  { icon: TrendingUp, title: "Growth & Enablement", services: ["Startup Acceleration", "Incubation Support", "AI Training"], accent: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/20" },
];

// ─── Carousel Component ───────────────────────────────────────────────────────
function ServiceCarousel() {
  const [current, setCurrent] = useState(0);
  const total = serviceFlashcards.length;
  const visibleCount = 3;

  const prev = () => setCurrent((c) => (c - 1 + total) % total);
  const next = () => setCurrent((c) => (c + 1) % total);

  const visible = Array.from({ length: visibleCount }, (_, i) =>
    serviceFlashcards[(current + i) % total]
  );

  return (
    <div className="relative">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {visible.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={`${current}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                className={`rounded-2xl border ${card.border} bg-white/[0.03] backdrop-blur-xl p-7 group`}
              >
                <div className={`w-11 h-11 rounded-xl ${card.bg} flex items-center justify-center mb-4`}>
                  <Icon size={20} className={card.accent} />
                </div>
                <h4 className="font-heading font-semibold text-white text-base mb-3">{card.title}</h4>
                <ul className="space-y-1.5">
                  {card.services.map((s) => (
                    <li key={s} className={`text-xs flex items-center gap-2 ${card.accent}`}>
                      <span className="w-1 h-1 rounded-full bg-current flex-shrink-0" />
                      <span className="text-slate-400">{s}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 mt-8">
        <button
          onClick={prev}
          className="w-10 h-10 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft size={18} className="text-white" />
        </button>
        <div className="flex gap-1.5">
          {serviceFlashcards.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-indigo-400" : "w-1.5 bg-white/20"}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        <button
          onClick={next}
          className="w-10 h-10 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          aria-label="Next"
        >
          <ChevronRight size={18} className="text-white" />
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function SolutionsPage() {
  const [pageData, setPageData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    axios.get(`${API}/pages/solutions`).then(r => setPageData(r.data)).catch(() => {});
  }, []);

  const getSection = (key, fallback = "") => {
    if (!pageData) return fallback;
    const s = pageData.sections?.find(s => s.key === key);
    return s?.value || fallback;
  };

  const active = ecosystemSolutions[activeTab];
  const ActiveIcon = active.icon;

  return (
    <div data-testid="solutions-page">
      {/* ── Hero ── */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12 overflow-hidden" data-testid="solutions-hero">
        <FloatingOrbs />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-600/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative">
          <AnimatedSection>
            <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-4 block">Ecosystem Solutions</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 max-w-4xl">
              {getSection("headline", "One Ecosystem. Every AI Solution Your Business Needs.")}
            </h1>
            <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
              {getSection("subheadline", "brgt.ai brings together five powerful solution domains — each purpose-built, deeply integrated, and delivered as a unified AI ecosystem that grows with your business.")}
            </p>
          </AnimatedSection>

          {/* Solution tab pills */}
          <AnimatedSection delay={0.2}>
            <div className="flex flex-wrap gap-2 mt-10">
              {ecosystemSolutions.map((sol, i) => {
                const TabIcon = sol.icon;
                return (
                  <button
                    key={sol.id}
                    onClick={() => setActiveTab(i)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                      activeTab === i
                        ? `bg-white/10 ${sol.accentBorder} ${sol.accentColor}`
                        : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <TabIcon size={14} />
                    <span className="hidden sm:inline">{sol.title.split(" ").slice(0, 2).join(" ")}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </button>
                );
              })}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Active Solution Detail ── */}
      <section className="px-6 md:px-12 pb-8" data-testid="solution-detail">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto"
          >
            <div className={`rounded-3xl border ${active.accentBorder} bg-white/[0.02] overflow-hidden`}>
              {/* Top gradient bar */}
              <div className={`h-1 w-full bg-gradient-to-r ${active.gradientFrom} ${active.gradientTo}`} />

              <div className="p-8 md:p-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Left — content */}
                <div className="lg:col-span-7 flex flex-col gap-6">
                  <div>
                    <div className={`inline-flex items-center gap-2 ${active.accentBg} border ${active.accentBorder} rounded-full px-4 py-1.5 mb-4`}>
                      <ActiveIcon size={15} className={active.accentColor} />
                      <span className={`text-xs font-semibold ${active.accentColor}`}>{active.tagline}</span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">{active.title}</h2>
                    <p className="text-slate-300 leading-relaxed">{active.description}</p>
                  </div>

                  {/* Capabilities */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Core Capabilities</h3>
                    <ul className="space-y-2.5">
                      {active.capabilities.map((cap) => (
                        <li key={cap} className="flex items-start gap-3 text-sm text-slate-300">
                          <Check size={15} className={`${active.accentColor} flex-shrink-0 mt-0.5`} />
                          {cap}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Use cases */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Industries & Use Cases</h3>
                    <div className="flex flex-wrap gap-2">
                      {active.useCases.map((uc) => (
                        <span
                          key={uc}
                          className={`text-xs px-3 py-1.5 rounded-full border ${active.accentBorder} ${active.accentBg} ${active.accentColor}`}
                        >
                          {uc}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right — illustration + impact */}
                <div className="lg:col-span-5 flex flex-col gap-6">
                  {/* Illustration */}
                  <div className={`rounded-2xl border ${active.accentBorder} overflow-hidden`}>
                    {active.illustration}
                  </div>

                  {/* Business impact cards */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Business Impact</h3>
                    <div className="grid grid-cols-1 gap-3">
                      {active.businessImpact.map((impact) => {
                        const ImpactIcon = impact.icon;
                        return (
                          <div
                            key={impact.label}
                            className={`flex items-center gap-4 rounded-xl border ${active.accentBorder} ${active.accentBg} px-4 py-3`}
                          >
                            <div className={`w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0`}>
                              <ImpactIcon size={17} className={active.accentColor} />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-white">{impact.label}</div>
                              <div className="text-xs text-slate-400">{impact.sub}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </section>

      {/* ── All Solutions Grid ── */}
      <AnimatedSection>
        <section className="py-16 md:py-24 px-6 md:px-12" data-testid="solutions-grid">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12 text-center">
              <span className="text-xs font-semibold tracking-wider uppercase text-indigo-400 mb-3 block">All Solutions</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">The Full Ecosystem at a Glance</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ecosystemSolutions.map((sol, i) => {
                const SolIcon = sol.icon;
                return (
                  <motion.button
                    key={sol.id}
                    onClick={() => {
                      setActiveTab(i);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    whileHover={{ y: -4, scale: 1.01 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className={`text-left rounded-2xl border ${sol.accentBorder} bg-white/[0.03] p-6 group cursor-pointer`}
                    data-testid={`solution-card-${sol.id}`}
                  >
                    <div className={`w-11 h-11 rounded-xl ${sol.accentBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <SolIcon size={20} className={sol.accentColor} />
                    </div>
                    <h3 className="font-heading font-semibold text-white text-base mb-2">{sol.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">{sol.description}</p>
                    <div className={`mt-4 flex items-center gap-1 text-xs font-medium ${sol.accentColor}`}>
                      <span>Explore</span>
                      <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* ── Service Capabilities Carousel ── */}
      <AnimatedSection>
        <section className="py-16 md:py-24 px-6 md:px-12 bg-surface-raised border-t border-white/5" data-testid="services-carousel">
          <div className="max-w-7xl mx-auto">
            <div className="mb-12 text-center">
              <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-3 block">How We Deliver</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Our Service Capabilities</h2>
              <p className="text-slate-400 max-w-xl mx-auto text-sm leading-relaxed">
                Every ecosystem solution is backed by brgt.ai's core service practices — from strategy to responsible AI.
              </p>
            </div>
            <ServiceCarousel />
          </div>
        </section>
      </AnimatedSection>

      {/* ── CTA ── */}
      <AnimatedSection>
        <section className="relative py-20 px-6 md:px-12 bg-navy text-center overflow-hidden" data-testid="solutions-cta">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <motion.div
              className="absolute w-80 h-80 rounded-full blur-3xl bg-amber/10"
              animate={{ x: [0, 60, 0], y: [0, -30, 0] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
              style={{ left: "5%", top: "-10%" }}
            />
            <motion.div
              className="absolute w-64 h-64 rounded-full blur-3xl bg-indigo-500/10"
              animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
              transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
              style={{ right: "10%", bottom: "-5%" }}
            />
          </div>
          <div className="max-w-3xl mx-auto relative">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">Ready to Deploy Your AI Ecosystem?</h2>
            <p className="text-slate-300 mb-8 leading-relaxed">
              Every business is unique. Let's identify which solutions fit your operations and engineer the perfect combination.
            </p>
            <Link
              to="/contact"
              data-testid="solutions-contact-btn"
              className="inline-flex items-center gap-2 bg-amber text-navy hover:bg-amber-light rounded-full px-8 py-3.5 font-semibold transition-all"
            >
              Start the Conversation <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
}
